import type { ReactNode } from 'react';
import { CONFIG } from '@h2h/shared';
import { cn } from '../../lib/cn';
import { ordinalSuffix } from '../../lib/format';
import { useHaptics } from '../../hooks/useHaptics';
import { MINI_LEAGUE_COLORS, MINI_LEAGUE_ICONS } from '../../components/ui/bits';
import { Check, ChevronLeft } from '../../components/ui/icons';

/** Standard back-nav header used across the leagues flow. */
export function LeagueNav({
  label,
  onBack,
  right
}: {
  label: string;
  onBack: () => void;
  right?: ReactNode;
}) {
  const { impact } = useHaptics();
  return (
    <div className="flex items-center justify-between pt-2 md:pt-5">
      <button
        type="button"
        onClick={() => {
          impact('light');
          onBack();
        }}
        className="tap -ml-1 flex items-center gap-[2px] text-ink"
      >
        <ChevronLeft width={22} height={22} strokeWidth={2.4} />
        <span className="text-[16px] font-semibold tracking-snug">{label}</span>
      </button>
      {right ?? <span className="w-10" />}
    </div>
  );
}

/** A centred "Cancel · title" modal-style header for the create/join flows. */
export function ModalNav({
  title,
  onCancel,
  cancelLabel = 'Cancel'
}: {
  title: string;
  onCancel: () => void;
  cancelLabel?: string;
}) {
  const { impact } = useHaptics();
  return (
    <div className="flex items-center justify-between pt-2 md:pt-5">
      <button
        type="button"
        onClick={() => {
          impact('light');
          onCancel();
        }}
        className="tap text-[16px] font-semibold tracking-snug text-ink2"
      >
        {cancelLabel}
      </button>
      <span className="text-[14px] font-bold tracking-snug text-ink">{title}</span>
      <span className="w-[52px]" />
    </div>
  );
}

/** Choose one of the five icon keys. */
export function IconPicker({ value, onChange }: { value: string; onChange: (key: string) => void }) {
  const { impact } = useHaptics();
  return (
    <div className="mt-[11px] flex gap-[10px]" data-segment role="tablist">
      {CONFIG.MINI_LEAGUE.ICONS.map((key) => {
        const Icon = MINI_LEAGUE_ICONS[key] ?? MINI_LEAGUE_ICONS.trophy;
        const active = key === value;
        return (
          <button
            key={key}
            type="button"
            aria-label={key}
            onClick={() => {
              impact('selection');
              onChange(key);
            }}
            className={cn(
              'tap grid h-[52px] w-[52px] place-items-center rounded-[15px] transition-colors',
              active ? 'border-2 border-accent bg-accent-wash text-accent-ink' : 'bg-fill2 text-[#52525A]'
            )}
          >
            <Icon width={23} height={23} strokeWidth={2} />
          </button>
        );
      })}
    </div>
  );
}

/** Choose one of the six colour keys. */
export function ColorPicker({ value, onChange }: { value: string; onChange: (key: string) => void }) {
  const { impact } = useHaptics();
  return (
    <div className="mt-[11px] flex gap-3" data-segment role="tablist">
      {CONFIG.MINI_LEAGUE.COLORS.map((key) => {
        const c = MINI_LEAGUE_COLORS[key] ?? MINI_LEAGUE_COLORS.green;
        const active = key === value;
        return (
          <button
            key={key}
            type="button"
            aria-label={key}
            onClick={() => {
              impact('selection');
              onChange(key);
            }}
            style={{
              backgroundImage: `linear-gradient(140deg, ${c.from}, ${c.to})`,
              boxShadow: active ? `0 0 0 2px #fff, 0 0 0 4px ${c.from}` : undefined
            }}
            className="tap h-[42px] w-[42px] rounded-full"
          >
            {active && (
              <span className="grid h-full w-full place-items-center">
                <Check width={16} height={16} strokeWidth={3} className="text-white" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** A rank number with its ordinal suffix, e.g. 2 → "2nd". */
export function placeLabel(rank: number): { n: number; suffix: string } {
  return { n: rank, suffix: ordinalSuffix(rank) };
}
